/**
 * Vault Page
 *
 * Secure credential management for agents and integrations.
 * Uses encrypted storage with pgsodium for secrets.
 *
 * Includes two sections:
 * - Secrets: General credentials (API keys, tokens, passwords, etc.)
 * - LLM Keys: Provider-specific LLM API keys (Anthropic, OpenAI, Google)
 */

import { type Component, createResource, createSignal, For, Show } from 'solid-js';
import {
  createUserVaultSecret,
  createVaultSecret,
  deleteUserVaultSecret,
  deleteVaultSecret,
  fetchUserVaultSecrets,
  fetchVaultSecrets,
  revealVaultSecret,
  revokeUserVaultSecret,
  revokeVaultSecret,
  rotateUserVaultSecret,
  rotateVaultSecret,
  type UserVaultSecret,
  type VaultSecret,
  type VaultSecretType,
} from '../services/api';
import {
  detectProviderFromKey,
  getProviderColorClasses,
  LLM_PROVIDER_PLACEHOLDERS,
  type LLMProviderPlaceholder,
} from '../constants/llm-providers.ts';

const SECRET_TYPES: { value: VaultSecretType; label: string; icon: string }[] = [
  {
    value: 'api_key',
    label: 'API Key',
    icon: 'M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z',
  },
  {
    value: 'oauth_token',
    label: 'OAuth Token',
    icon: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
  },
  {
    value: 'bearer_token',
    label: 'Bearer Token',
    icon: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
  },
  {
    value: 'password',
    label: 'Password',
    icon: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z',
  },
  {
    value: 'ssh_key',
    label: 'SSH Key',
    icon: 'M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z',
  },
  {
    value: 'certificate',
    label: 'Certificate',
    icon: 'M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z',
  },
  {
    value: 'env_var',
    label: 'Environment Variable',
    icon: 'M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4',
  },
  {
    value: 'webhook_secret',
    label: 'Webhook Secret',
    icon: 'M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1',
  },
  {
    value: 'encryption_key',
    label: 'Encryption Key',
    icon: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z',
  },
  {
    value: 'other',
    label: 'Other',
    icon: 'M8 7v8a2 2 0 002 2h6M8 7V5a2 2 0 012-2h4.586a1 1 0 01.707.293l4.414 4.414a1 1 0 01.293.707V15a2 2 0 01-2 2h-2M8 7H6a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2v-2',
  },
];

type TabType = 'llm-keys' | 'secrets';

const Vault: Component = () => {
  const [activeTab, setActiveTab] = createSignal<TabType>('llm-keys');

  // Secrets tab state
  const [secrets, { refetch: refetchSecrets }] = createResource(() => fetchVaultSecrets().then((r) => r.items));
  const [showCreate, setShowCreate] = createSignal(false);
  const [showRotate, setShowRotate] = createSignal<string | null>(null);
  const [revealedSecrets, setRevealedSecrets] = createSignal<Record<string, string>>({});
  const [error, setError] = createSignal<string | null>(null);
  const [loading, setLoading] = createSignal(false);

  // Create form state
  const [newName, setNewName] = createSignal('');
  const [newType, setNewType] = createSignal<VaultSecretType>('api_key');
  const [newSecret, setNewSecret] = createSignal('');
  const [newDescription, setNewDescription] = createSignal('');
  const [newTags, setNewTags] = createSignal('');

  // Rotate form state
  const [rotateSecretValue, setRotateSecretValue] = createSignal('');

  // LLM Keys tab state
  const [llmSecrets, { refetch: refetchLLMSecrets }] = createResource(() =>
    fetchUserVaultSecrets().then((r) => r.items || []),
  );
  const [llmLoading, setLLMLoading] = createSignal<string | null>(null);
  const [showLLMRotate, setShowLLMRotate] = createSignal<string | null>(null);
  const [llmRotateValue, setLLMRotateValue] = createSignal('');
  const [inputValues, setInputValues] = createSignal<Record<string, string>>({});

  const getSecretTypeInfo = (type: VaultSecretType) =>
    SECRET_TYPES.find((t) => t.value === type) || SECRET_TYPES[SECRET_TYPES.length - 1];

  // ===== Secrets Tab Functions =====
  const handleCreate = async () => {
    setError(null);
    setLoading(true);

    try {
      await createVaultSecret({
        name: newName(),
        type: newType(),
        secret: newSecret(),
        description: newDescription() || undefined,
        tags: newTags()
          ? newTags()
              .split(',')
              .map((t) => t.trim())
          : undefined,
      });

      // Reset form
      setNewName('');
      setNewType('api_key');
      setNewSecret('');
      setNewDescription('');
      setNewTags('');
      setShowCreate(false);
      refetchSecrets();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create secret');
    } finally {
      setLoading(false);
    }
  };

  const handleReveal = async (id: string) => {
    try {
      const result = await revealVaultSecret(id);
      setRevealedSecrets({ ...revealedSecrets(), [id]: result.secret });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reveal secret');
    }
  };

  const handleHide = (id: string) => {
    const current = { ...revealedSecrets() };
    delete current[id];
    setRevealedSecrets(current);
  };

  const handleRotate = async (id: string) => {
    setError(null);
    setLoading(true);

    try {
      await rotateVaultSecret(id, rotateSecretValue());
      setRotateSecretValue('');
      setShowRotate(null);
      // Clear revealed value since it's been rotated
      handleHide(id);
      refetchSecrets();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to rotate secret');
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async (id: string) => {
    if (!confirm('Are you sure you want to revoke this secret? This will mark it as inactive.')) {
      return;
    }

    try {
      await revokeVaultSecret(id);
      refetchSecrets();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke secret');
    }
  };

  const handleDelete = async (id: string) => {
    if (
      !confirm('Are you sure you want to permanently delete this secret? This cannot be undone.')
    ) {
      return;
    }

    try {
      await deleteVaultSecret(id);
      refetchSecrets();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete secret');
    }
  };

  // ===== LLM Keys Tab Functions =====
  const getSecretForProvider = (provider: LLMProviderPlaceholder): UserVaultSecret | undefined => {
    return llmSecrets()?.find((s) => s.name === provider.vaultKeyName);
  };

  const handleKeyInput = (provider: LLMProviderPlaceholder, value: string) => {
    setInputValues((prev) => ({ ...prev, [provider.id]: value }));

    // Auto-detect if key was pasted into wrong field
    if (value.length > 10) {
      const detected = detectProviderFromKey(value);
      if (detected && detected.id !== provider.id) {
        setInputValues((prev) => ({
          ...prev,
          [provider.id]: '',
          [detected.id]: value,
        }));
      }
    }
  };

  const handleSaveLLMKey = async (provider: LLMProviderPlaceholder) => {
    const value = inputValues()[provider.id];
    if (!value?.trim()) return;

    setError(null);
    setLLMLoading(provider.id);

    try {
      await createUserVaultSecret({
        name: provider.vaultKeyName,
        type: 'llm_api_key',
        provider: provider.id,
        secret: value.trim(),
        description: `${provider.name} API key`,
      });

      setInputValues((prev) => ({ ...prev, [provider.id]: '' }));
      refetchLLMSecrets();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save API key');
    } finally {
      setLLMLoading(null);
    }
  };

  const handleLLMRotate = async (secretId: string) => {
    if (!llmRotateValue().trim()) return;

    setError(null);
    setLLMLoading('rotate');

    try {
      await rotateUserVaultSecret(secretId, llmRotateValue());
      setLLMRotateValue('');
      setShowLLMRotate(null);
      refetchLLMSecrets();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to rotate key');
    } finally {
      setLLMLoading(null);
    }
  };

  const handleLLMRevoke = async (secretId: string) => {
    if (!confirm('Are you sure you want to revoke this API key? It will no longer be usable.')) {
      return;
    }

    try {
      await revokeUserVaultSecret(secretId);
      refetchLLMSecrets();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to revoke key');
    }
  };

  const handleLLMDelete = async (secretId: string) => {
    if (
      !confirm('Are you sure you want to permanently delete this API key? This cannot be undone.')
    ) {
      return;
    }

    try {
      await deleteUserVaultSecret(secretId);
      refetchLLMSecrets();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete key');
    }
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-500/20 text-green-400';
      case 'expired':
        return 'bg-yellow-500/20 text-yellow-400';
      case 'revoked':
        return 'bg-red-500/20 text-red-400';
      case 'rotated':
        return 'bg-blue-500/20 text-blue-400';
      default:
        return 'bg-gray-500/20 text-gray-400';
    }
  };

  return (
    <div>
      <div class="flex items-center justify-between mb-6">
        <div>
          <h1 class="text-2xl font-bold">Vault</h1>
          <p class="text-gray-400 mt-1">
            Securely store and manage credentials for agents and integrations
          </p>
        </div>
        <Show when={activeTab() === 'secrets'}>
          <button
            onClick={() => setShowCreate(true)}
            class="px-4 py-2 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-medium rounded-lg hover:from-purple-500 hover:to-pink-500 transition-all shadow-lg shadow-purple-500/20"
          >
            Add Secret
          </button>
        </Show>
      </div>

      {/* Tabs */}
      <div class="flex gap-1 mb-6 bg-gray-800/50 p-1 rounded-xl w-fit">
        <button
          onClick={() => setActiveTab('llm-keys')}
          class={`px-4 py-2 rounded-lg font-medium transition-all ${
            activeTab() === 'llm-keys'
              ? 'bg-gradient-to-r from-amber-500 to-orange-500 text-white shadow-lg'
              : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
          }`}
        >
          LLM Keys
        </button>
        <button
          onClick={() => setActiveTab('secrets')}
          class={`px-4 py-2 rounded-lg font-medium transition-all ${
            activeTab() === 'secrets'
              ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white shadow-lg'
              : 'text-gray-400 hover:text-white hover:bg-gray-700/50'
          }`}
        >
          Secrets
        </button>
      </div>

      {/* Error Message */}
      <Show when={error()}>
        <div class="mb-4 p-4 bg-red-500/20 border border-red-500/50 rounded-xl">
          <div class="flex items-center justify-between">
            <p class="text-red-400">{error()}</p>
            <button onClick={() => setError(null)} class="text-gray-500 hover:text-gray-300">
              <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>
      </Show>

      {/* LLM Keys Tab Content */}
      <Show when={activeTab() === 'llm-keys'}>
        <div class="space-y-4">
          <For each={LLM_PROVIDER_PLACEHOLDERS}>
            {(provider) => {
              const colors = getProviderColorClasses(provider.color);
              const existingSecret = () => getSecretForProvider(provider);
              const inputValue = () => inputValues()[provider.id] || '';
              const isLoading = () => llmLoading() === provider.id;

              return (
                <div class={`bg-gray-800/50 border ${colors.border} rounded-xl overflow-hidden`}>
                  {/* Provider Header */}
                  <div class="p-4 border-b border-gray-700/50">
                    <div class="flex items-center justify-between">
                      <div class="flex items-center gap-3">
                        <div
                          class={`w-10 h-10 rounded-lg ${colors.bg} flex items-center justify-center`}
                        >
                          <span class={`text-lg font-bold ${colors.text}`}>
                            {provider.name.charAt(0)}
                          </span>
                        </div>
                        <div>
                          <h3 class="font-semibold text-white">{provider.name}</h3>
                          <p class="text-sm text-gray-400">{provider.exampleModels}</p>
                        </div>
                      </div>

                      {/* Status Badge */}
                      <Show
                        when={existingSecret()}
                        fallback={
                          <span class="px-3 py-1 text-xs bg-gray-700 text-gray-400 rounded-full">
                            Not configured
                          </span>
                        }
                      >
                        <span
                          class={`px-3 py-1 text-xs rounded-full ${
                            existingSecret()?.status === 'active'
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-red-500/20 text-red-400'
                          }`}
                        >
                          {existingSecret()?.status === 'active' ? 'Active' : 'Revoked'}
                        </span>
                      </Show>
                    </div>
                  </div>

                  {/* Content */}
                  <div class="p-4">
                    <Show
                      when={!existingSecret()}
                      fallback={
                        /* Existing Key Management */
                        <div>
                          <div class="flex items-center justify-between mb-3">
                            <div class="flex items-center gap-2">
                              <svg
                                class="w-4 h-4 text-green-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                  stroke-width="2"
                                  d="M5 13l4 4L19 7"
                                />
                              </svg>
                              <span class="text-sm text-gray-300">API key configured</span>
                            </div>
                            <div class="flex items-center gap-2">
                              <Show when={existingSecret()?.status === 'active'}>
                                <button
                                  type="button"
                                  onClick={() => setShowLLMRotate(existingSecret()!.id)}
                                  class="px-3 py-1.5 text-sm text-blue-400 hover:text-blue-300 transition-colors"
                                >
                                  Rotate
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleLLMRevoke(existingSecret()!.id)}
                                  class="px-3 py-1.5 text-sm text-yellow-400 hover:text-yellow-300 transition-colors"
                                >
                                  Revoke
                                </button>
                              </Show>
                              <button
                                type="button"
                                onClick={() => handleLLMDelete(existingSecret()!.id)}
                                class="px-3 py-1.5 text-sm text-red-400 hover:text-red-300 transition-colors"
                              >
                                Delete
                              </button>
                            </div>
                          </div>

                          {/* Usage Stats */}
                          <div class="text-xs text-gray-500">
                            <Show when={existingSecret()?.lastUsed}>
                              Last used: {new Date(existingSecret()!.lastUsed!).toLocaleDateString()}
                              {' | '}
                            </Show>
                            Used {existingSecret()?.useCount || 0} times
                          </div>
                        </div>
                      }
                    >
                      {/* Add Key Form */}
                      <div>
                        <p class="text-sm text-gray-400 mb-3">{provider.description}</p>

                        {/* Features */}
                        <div class="flex flex-wrap gap-2 mb-4">
                          <For each={provider.features}>
                            {(feature) => (
                              <span class="px-2 py-0.5 text-xs bg-gray-700/50 text-gray-400 rounded">
                                {feature}
                              </span>
                            )}
                          </For>
                        </div>

                        {/* Input */}
                        <div class="flex gap-3">
                          <input
                            type="password"
                            value={inputValue()}
                            onInput={(e) => handleKeyInput(provider, e.currentTarget.value)}
                            placeholder={`Paste your ${provider.name} API key...`}
                            class="flex-1 px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-amber-500 font-mono text-sm"
                          />
                          <button
                            type="button"
                            onClick={() => handleSaveLLMKey(provider)}
                            disabled={isLoading() || !inputValue().trim()}
                            class={`px-6 py-3 bg-gradient-to-r ${colors.gradient} hover:opacity-90 text-white font-medium rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed`}
                          >
                            {isLoading() ? (
                              <svg class="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                                <circle
                                  class="opacity-25"
                                  cx="12"
                                  cy="12"
                                  r="10"
                                  stroke="currentColor"
                                  stroke-width="4"
                                />
                                <path
                                  class="opacity-75"
                                  fill="currentColor"
                                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                />
                              </svg>
                            ) : (
                              'Save'
                            )}
                          </button>
                        </div>

                        {/* Get Key Link */}
                        <div class="mt-3 flex items-center gap-2">
                          <span class="text-xs text-gray-500">Don't have a key?</span>
                          <a
                            href={provider.getKeyUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            class={`text-xs ${colors.text} hover:underline flex items-center gap-1`}
                          >
                            Get one from {provider.name}
                            <svg
                              class="w-3 h-3"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                stroke-linecap="round"
                                stroke-linejoin="round"
                                stroke-width="2"
                                d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
                              />
                            </svg>
                          </a>
                        </div>
                      </div>
                    </Show>
                  </div>
                </div>
              );
            }}
          </For>
        </div>

        {/* LLM Rotate Modal */}
        <Show when={showLLMRotate()}>
          <div
            class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
            onClick={() => setShowLLMRotate(null)}
          >
            <div
              class="bg-gray-800 border border-gray-700 rounded-2xl p-6 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 class="text-xl font-bold mb-4">Rotate API Key</h2>
              <p class="text-gray-400 text-sm mb-4">
                Enter the new API key. The old key will be replaced immediately.
              </p>

              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1.5">New API Key</label>
                <input
                  type="password"
                  value={llmRotateValue()}
                  onInput={(e) => setLLMRotateValue(e.currentTarget.value)}
                  placeholder="Paste your new API key..."
                  class="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-amber-500 font-mono text-sm"
                />
              </div>

              <div class="flex gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowLLMRotate(null);
                    setLLMRotateValue('');
                  }}
                  class="flex-1 px-4 py-2.5 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={() => handleLLMRotate(showLLMRotate()!)}
                  disabled={llmLoading() === 'rotate' || !llmRotateValue().trim()}
                  class="flex-1 px-4 py-2.5 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-medium rounded-lg transition-all disabled:opacity-50"
                >
                  {llmLoading() === 'rotate' ? 'Rotating...' : 'Rotate Key'}
                </button>
              </div>
            </div>
          </div>
        </Show>

        {/* Spending Limits Notice */}
        <div class="mt-8 p-4 bg-blue-500/10 border border-blue-500/30 rounded-xl">
          <div class="flex items-start gap-3">
            <svg
              class="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div class="text-sm">
              <p class="text-blue-300 font-medium">Set Spending Limits</p>
              <p class="text-gray-400 mt-1">
                To control costs, set spending limits directly on each provider's website. This
                ensures you never exceed your budget.
              </p>
            </div>
          </div>
        </div>
      </Show>

      {/* Secrets Tab Content */}
      <Show when={activeTab() === 'secrets'}>
        {/* Create Secret Modal */}
        <Show when={showCreate()}>
          <div
            class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
            onClick={() => setShowCreate(false)}
          >
            <div
              class="bg-gray-800 border border-gray-700 rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 class="text-xl font-bold mb-4">Add Secret</h2>

              <div class="space-y-4">
                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-1.5">Name</label>
                  <input
                    type="text"
                    value={newName()}
                    onInput={(e) => setNewName(e.currentTarget.value)}
                    placeholder="e.g., OpenAI API Key"
                    class="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-1.5">Type</label>
                  <select
                    value={newType()}
                    onChange={(e) => setNewType(e.currentTarget.value as VaultSecretType)}
                    class="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white focus:outline-none focus:ring-2 focus:ring-purple-500"
                  >
                    <For each={SECRET_TYPES}>
                      {(type) => <option value={type.value}>{type.label}</option>}
                    </For>
                  </select>
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-1.5">Secret Value</label>
                  <textarea
                    value={newSecret()}
                    onInput={(e) => setNewSecret(e.currentTarget.value)}
                    placeholder="Paste your secret here..."
                    rows={3}
                    class="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono text-sm"
                  />
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-1.5">
                    Description (optional)
                  </label>
                  <input
                    type="text"
                    value={newDescription()}
                    onInput={(e) => setNewDescription(e.currentTarget.value)}
                    placeholder="What is this secret used for?"
                    class="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>

                <div>
                  <label class="block text-sm font-medium text-gray-300 mb-1.5">
                    Tags (optional)
                  </label>
                  <input
                    type="text"
                    value={newTags()}
                    onInput={(e) => setNewTags(e.currentTarget.value)}
                    placeholder="production, openai, billing (comma-separated)"
                    class="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500"
                  />
                </div>
              </div>

              <div class="flex gap-3 mt-6">
                <button
                  onClick={() => setShowCreate(false)}
                  class="flex-1 px-4 py-2.5 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreate}
                  disabled={loading() || !newName().trim() || !newSecret().trim()}
                  class="flex-1 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-medium rounded-lg transition-all disabled:opacity-50"
                >
                  {loading() ? 'Adding...' : 'Add Secret'}
                </button>
              </div>
            </div>
          </div>
        </Show>

        {/* Rotate Secret Modal */}
        <Show when={showRotate()}>
          <div
            class="fixed inset-0 bg-black/50 flex items-center justify-center z-50"
            onClick={() => setShowRotate(null)}
          >
            <div
              class="bg-gray-800 border border-gray-700 rounded-2xl p-6 w-full max-w-md"
              onClick={(e) => e.stopPropagation()}
            >
              <h2 class="text-xl font-bold mb-4">Rotate Secret</h2>
              <p class="text-gray-400 text-sm mb-4">
                Enter the new secret value. The old secret will be marked as rotated.
              </p>

              <div>
                <label class="block text-sm font-medium text-gray-300 mb-1.5">New Secret Value</label>
                <textarea
                  value={rotateSecretValue()}
                  onInput={(e) => setRotateSecretValue(e.currentTarget.value)}
                  placeholder="Paste your new secret here..."
                  rows={3}
                  class="w-full px-4 py-3 bg-gray-700/50 border border-gray-600 rounded-xl text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-500 font-mono text-sm"
                />
              </div>

              <div class="flex gap-3 mt-6">
                <button
                  onClick={() => {
                    setShowRotate(null);
                    setRotateSecretValue('');
                  }}
                  class="flex-1 px-4 py-2.5 bg-gray-700 hover:bg-gray-600 text-white font-medium rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={() => handleRotate(showRotate()!)}
                  disabled={loading() || !rotateSecretValue().trim()}
                  class="flex-1 px-4 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 text-white font-medium rounded-lg transition-all disabled:opacity-50"
                >
                  {loading() ? 'Rotating...' : 'Rotate Secret'}
                </button>
              </div>
            </div>
          </div>
        </Show>

        {/* Secrets List */}
        <div class="bg-gray-800/50 border border-gray-700 rounded-xl overflow-hidden">
          <Show
            when={!secrets.loading}
            fallback={
              <div class="p-8 text-center">
                <svg
                  class="animate-spin w-8 h-8 text-purple-500 mx-auto"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    class="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    stroke-width="4"
                  />
                  <path
                    class="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
              </div>
            }
          >
            <Show
              when={secrets()?.length}
              fallback={
                <div class="p-8 text-center">
                  <svg
                    class="w-12 h-12 text-gray-600 mx-auto mb-3"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
                    />
                  </svg>
                  <p class="text-gray-400">No secrets stored yet</p>
                  <p class="text-gray-500 text-sm mt-1">Add your first secret to get started</p>
                </div>
              }
            >
              <div class="divide-y divide-gray-700/50">
                <For each={secrets()}>
                  {(secret: VaultSecret) => {
                    const typeInfo = getSecretTypeInfo(secret.type);
                    const revealed = revealedSecrets()[secret.id];

                    return (
                      <div class="p-4 hover:bg-gray-700/20 transition-colors">
                        <div class="flex items-start justify-between gap-4">
                          <div class="flex items-start gap-3 flex-1 min-w-0">
                            {/* Type Icon */}
                            <div class="w-10 h-10 rounded-lg bg-purple-500/20 flex items-center justify-center flex-shrink-0">
                              <svg
                                class="w-5 h-5 text-purple-400"
                                fill="none"
                                stroke="currentColor"
                                viewBox="0 0 24 24"
                              >
                                <path
                                  stroke-linecap="round"
                                  stroke-linejoin="round"
                                  stroke-width="2"
                                  d={typeInfo.icon}
                                />
                              </svg>
                            </div>

                            {/* Secret Info */}
                            <div class="flex-1 min-w-0">
                              <div class="flex items-center gap-2">
                                <h3 class="font-medium truncate">{secret.name}</h3>
                                <span
                                  class={`px-2 py-0.5 text-xs rounded ${getStatusColor(
                                    secret.status,
                                  )}`}
                                >
                                  {secret.status}
                                </span>
                              </div>
                              <p class="text-gray-400 text-sm">{typeInfo.label}</p>
                              <Show when={secret.description}>
                                <p class="text-gray-500 text-sm mt-1">{secret.description}</p>
                              </Show>

                              {/* Revealed Secret */}
                              <Show when={revealed}>
                                <div class="mt-3 flex items-center gap-2">
                                  <code class="flex-1 px-3 py-2 bg-gray-800 rounded-lg font-mono text-sm text-gray-300 overflow-x-auto">
                                    {revealed}
                                  </code>
                                  <button
                                    onClick={() => copyToClipboard(revealed)}
                                    class="px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white transition-colors"
                                    title="Copy to clipboard"
                                  >
                                    <svg
                                      class="w-4 h-4"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="2"
                                        d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                                      />
                                    </svg>
                                  </button>
                                  <button
                                    onClick={() => handleHide(secret.id)}
                                    class="px-3 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg text-white transition-colors"
                                    title="Hide secret"
                                  >
                                    <svg
                                      class="w-4 h-4"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                        stroke-width="2"
                                        d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                                      />
                                    </svg>
                                  </button>
                                </div>
                              </Show>

                              {/* Tags */}
                              <Show when={secret.tags?.length}>
                                <div class="flex gap-1.5 flex-wrap mt-2">
                                  <For each={secret.tags}>
                                    {(tag) => (
                                      <span class="px-2 py-0.5 bg-gray-700 text-gray-300 text-xs rounded">
                                        {tag}
                                      </span>
                                    )}
                                  </For>
                                </div>
                              </Show>

                              {/* Metadata */}
                              <div class="flex items-center gap-4 mt-2 text-xs text-gray-500">
                                <span>
                                  Created {new Date(secret.created_at).toLocaleDateString()}
                                </span>
                                <Show when={secret.last_used}>
                                  <span>
                                    Last used {new Date(secret.last_used!).toLocaleDateString()}
                                  </span>
                                </Show>
                                <span>Used {secret.use_count} times</span>
                              </div>
                            </div>
                          </div>

                          {/* Actions */}
                          <div class="flex items-center gap-2 flex-shrink-0">
                            <Show when={secret.status === 'active'}>
                              <Show when={!revealed} fallback={null}>
                                <button
                                  onClick={() => handleReveal(secret.id)}
                                  class="px-3 py-1.5 text-sm text-purple-400 hover:text-purple-300 transition-colors"
                                  title="Reveal secret"
                                >
                                  Reveal
                                </button>
                              </Show>
                              <button
                                onClick={() => setShowRotate(secret.id)}
                                class="px-3 py-1.5 text-sm text-blue-400 hover:text-blue-300 transition-colors"
                                title="Rotate secret"
                              >
                                Rotate
                              </button>
                              <button
                                onClick={() => handleRevoke(secret.id)}
                                class="px-3 py-1.5 text-sm text-yellow-400 hover:text-yellow-300 transition-colors"
                                title="Revoke secret"
                              >
                                Revoke
                              </button>
                            </Show>
                            <button
                              onClick={() => handleDelete(secret.id)}
                              class="px-3 py-1.5 text-sm text-red-400 hover:text-red-300 transition-colors"
                              title="Delete secret"
                            >
                              Delete
                            </button>
                          </div>
                        </div>
                      </div>
                    );
                  }}
                </For>
              </div>
            </Show>
          </Show>
        </div>
      </Show>

      {/* Security Notice */}
      <div class="mt-6 p-4 bg-amber-500/10 border border-amber-500/30 rounded-xl">
        <div class="flex items-start gap-3">
          <svg
            class="w-5 h-5 text-amber-400 mt-0.5 flex-shrink-0"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
            />
          </svg>
          <div class="text-sm">
            <p class="text-amber-300 font-medium">Encryption at Rest</p>
            <p class="text-gray-400 mt-1">
              All secrets are encrypted using AES-256-GCM before storage. Only you can decrypt and
              view your secrets. Keys are never logged or exposed.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Vault;
